<!-- Advanced Features Section -->
<section class="features-section" id="features">
  <div class="section-container">
    <div class="section-header">
      <div class="header-badge">
        <span class="badge-icon">⚡</span>
        <span class="badge-text">Powerful Features</span>
      </div>
      <h2 class="section-title">Everything You Need to Succeed</h2>
      <p class="section-subtitle">
        Our comprehensive platform provides all the tools and features needed
        to manage your educational institution effectively
      </p>
    </div>
    <div class="features-grid">
      <div class="feature-card" *ngFor="let feature of platformFeatures; let i = index" [class]="'feature-' + (i + 1)">
        <div class="feature-icon-wrapper">
          <span class="feature-icon">{{ feature.icon }}</span>
          <div class="feature-icon-bg"></div>
        </div>
        <div class="feature-content">
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-description">{{ feature.description }}</p>
        </div>
        <div class="feature-hover-effect"></div>
      </div>
    </div>
  </div>
</section>

<!-- Educational Platform Features Section -->
<section id="platform-features" class="educational-features-section">
  <div class="section-container">
    <div class="section-header">
      <span class="section-badge">🎓 Student Experience</span>
      <h2 class="section-title">Everything You Need to Succeed</h2>
      <p class="section-subtitle">
        Discover powerful tools and features designed specifically to support your learning journey and help you achieve your academic goals
      </p>
    </div>

    <div class="educational-features-grid">
      <!-- Live Class Sessions Card -->
      <div class="edu-feature-card live-classes">
        <div class="card-background">
          <div class="gradient-overlay"></div>
          <div class="floating-elements">
            <div class="floating-icon icon-1">📹</div>
            <div class="floating-icon icon-2">👥</div>
            <div class="floating-icon icon-3">💬</div>
          </div>
        </div>
        <div class="card-content">
          <div class="feature-header">
            <div class="feature-icon-large">🎥</div>
            <h3 class="feature-title-large">Live Interactive Classes</h3>
          </div>
          <p class="feature-description-large">
            Engage in real-time interactive sessions with HD video, screen sharing, breakout rooms, and collaborative whiteboards
          </p>
          <div class="feature-highlights">
            <div class="highlight-item">
              <span class="highlight-icon">✨</span>
              <span>HD Video Quality</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">🔄</span>
              <span>Screen Sharing</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">👥</span>
              <span>Breakout Rooms</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">📝</span>
              <span>Interactive Whiteboard</span>
            </div>
          </div>
        </div>
        <div class="card-glow-effect"></div>
      </div>

      <!-- Recorded Sessions Card -->
      <div class="edu-feature-card recorded-sessions">
        <div class="card-background">
          <div class="gradient-overlay"></div>
          <div class="floating-elements">
            <div class="floating-icon icon-1">⏯️</div>
            <div class="floating-icon icon-2">📚</div>
            <div class="floating-icon icon-3">🔍</div>
          </div>
        </div>
        <div class="card-content">
          <div class="feature-header">
            <div class="feature-icon-large">📼</div>
            <h3 class="feature-title-large">Smart Recorded Sessions</h3>
          </div>
          <p class="feature-description-large">
            Access comprehensive library of recorded classes with smart search, bookmarks, playback speed control, and note-taking
          </p>
          <div class="feature-highlights">
            <div class="highlight-item">
              <span class="highlight-icon">🔍</span>
              <span>Smart Search</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">⚡</span>
              <span>Variable Speed</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">📌</span>
              <span>Bookmarks</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">📝</span>
              <span>Note Taking</span>
            </div>
          </div>
        </div>
        <div class="card-glow-effect"></div>
      </div>

      <!-- AI-Powered Summaries Card -->
      <div class="edu-feature-card ai-summaries">
        <div class="card-background">
          <div class="gradient-overlay"></div>
          <div class="floating-elements">
            <div class="floating-icon icon-1">🤖</div>
            <div class="floating-icon icon-2">📊</div>
            <div class="floating-icon icon-3">⚡</div>
          </div>
        </div>
        <div class="card-content">
          <div class="feature-header">
            <div class="feature-icon-large">🧠</div>
            <h3 class="feature-title-large">AI-Powered Insights</h3>
          </div>
          <p class="feature-description-large">
            Get intelligent lesson summaries, key points extraction, quiz generation, and personalized learning recommendations
          </p>
          <div class="feature-highlights">
            <div class="highlight-item">
              <span class="highlight-icon">🎯</span>
              <span>Key Points</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">❓</span>
              <span>Auto Quiz</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">📈</span>
              <span>Progress Tracking</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">🎓</span>
              <span>Smart Recommendations</span>
            </div>
          </div>
        </div>
        <div class="card-glow-effect"></div>
      </div>

      <!-- Virtual Classroom Card -->
      <div class="edu-feature-card virtual-classroom">
        <div class="card-background">
          <div class="gradient-overlay"></div>
          <div class="floating-elements">
            <div class="floating-icon icon-1">🌐</div>
            <div class="floating-icon icon-2">🎮</div>
            <div class="floating-icon icon-3">🏆</div>
          </div>
        </div>
        <div class="card-content">
          <div class="feature-header">
            <div class="feature-icon-large">🏫</div>
            <h3 class="feature-title-large">Virtual Classroom</h3>
          </div>
          <p class="feature-description-large">
            Immersive 3D virtual environments with gamification, interactive labs, and collaborative project spaces
          </p>
          <div class="feature-highlights">
            <div class="highlight-item">
              <span class="highlight-icon">🎮</span>
              <span>Gamification</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">🧪</span>
              <span>Virtual Labs</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">🤝</span>
              <span>Collaboration</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">🏆</span>
              <span>Achievements</span>
            </div>
          </div>
        </div>
        <div class="card-glow-effect"></div>
      </div>

      <!-- Assessment & Analytics Card -->
      <div class="edu-feature-card assessments">
        <div class="card-background">
          <div class="gradient-overlay"></div>
          <div class="floating-elements">
            <div class="floating-icon icon-1">📊</div>
            <div class="floating-icon icon-2">📈</div>
            <div class="floating-icon icon-3">🎯</div>
          </div>
        </div>
        <div class="card-content">
          <div class="feature-header">
            <div class="feature-icon-large">📊</div>
            <h3 class="feature-title-large">Smart Assessments</h3>
          </div>
          <p class="feature-description-large">
            Advanced analytics with real-time performance tracking, adaptive testing, and detailed progress reports
          </p>
          <div class="feature-highlights">
            <div class="highlight-item">
              <span class="highlight-icon">📈</span>
              <span>Real-time Analytics</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">🎯</span>
              <span>Adaptive Testing</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">📋</span>
              <span>Detailed Reports</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">⚡</span>
              <span>Instant Feedback</span>
            </div>
          </div>
        </div>
        <div class="card-glow-effect"></div>
      </div>

      <!-- Mobile Learning Card -->
      <div class="edu-feature-card mobile-learning">
        <div class="card-background">
          <div class="gradient-overlay"></div>
          <div class="floating-elements">
            <div class="floating-icon icon-1">📱</div>
            <div class="floating-icon icon-2">☁️</div>
            <div class="floating-icon icon-3">🔄</div>
          </div>
        </div>
        <div class="card-content">
          <div class="feature-header">
            <div class="feature-icon-large">📱</div>
            <h3 class="feature-title-large">Mobile Learning Hub</h3>
          </div>
          <p class="feature-description-large">
            Learn anywhere with native mobile apps, offline capabilities, cloud sync, and push notifications
          </p>
          <div class="feature-highlights">
            <div class="highlight-item">
              <span class="highlight-icon">📱</span>
              <span>Native Apps</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">📡</span>
              <span>Offline Mode</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">☁️</span>
              <span>Cloud Sync</span>
            </div>
            <div class="highlight-item">
              <span class="highlight-icon">🔔</span>
              <span>Smart Notifications</span>
            </div>
          </div>
        </div>
        <div class="card-glow-effect"></div>
      </div>
    </div>
  </div>
</section>